import request from '../../utils/request';
import { Result } from '../../models/general';
import {
  WpsDeadlineView,
  SifPreflightView,
  WpsSifSubmissionView,
  KsaWpsSubmissionView,
  KsaMudadMarkUploadedInput,
  WpsConfirmView,
  WpsConfirmWarning,
  WpsCoverageView,
  WpsDueMonthView,
  WpsHoldReason,
  WpsLineReleaseReason,
  WpsPaidOutsideInput,
  WpsPaidOutsideView,
  WpsSettingsInput,
  WpsSettingsView,
  WpsVoidReason,
} from '../../models/payroll';
import { AxiosResponse } from 'axios';

export async function getWpsDeadline(year: number, month: number): Promise<Result<WpsDeadlineView>> {
  const response = await request.get<Result<WpsDeadlineView>>(`Wps/Deadline/${year}/${month}`);
  return response.data;
}

export async function getSifPreflight(year: number, month: number): Promise<Result<SifPreflightView>> {
  const response = await request.get<Result<SifPreflightView>>(`Wps/Preflight/${year}/${month}`);
  return response.data;
}

export async function generateSif(year: number, month: number): Promise<void> {
  const response: AxiosResponse<Blob> = await request.post(
    `Wps/GenerateSif/${year}/${month}`,
    {},
    {
      responseType: 'blob',
      headers: { 'Content-Type': 'application/json' },
      validateStatus: () => true,
    }
  );

  if (response.status !== 200) {
    const text = await (response.data as Blob).text();
    try {
      const json = JSON.parse(text);
      const messages = (json.errors || [])
        .map((e: { description?: string; message?: string }) => e.description || e.message || '')
        .filter(Boolean);
      throw new Error(messages.join('\n') || `SIF generation failed (${response.status})`);
    } catch (e) {
      if (e instanceof Error && e.message.includes('\n')) throw e;
      throw new Error(text || `SIF generation failed (${response.status})`);
    }
  }

  const headers = response.headers as Record<string, string>;
  const cd = headers['content-disposition'] || headers['Content-Disposition'];
  let fileName = `SIF_${year}_${String(month).padStart(2, '0')}_${Date.now()}.sif`;
  if (cd?.includes('filename=')) {
    try {
      const extracted = cd.split('filename=')[1].split(';')[0].trim().replace(/"/g, '');
      if (extracted) fileName = extracted;
    } catch { /* keep fallback */ }
  }

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export async function getWpsHistory(): Promise<Result<WpsSifSubmissionView[]>> {
  const response = await request.get<Result<WpsSifSubmissionView[]>>('Wps/History');
  return response.data;
}

export async function markMudadUploaded(
  year: number,
  month: number,
  input: KsaMudadMarkUploadedInput,
): Promise<Result<boolean>> {
  const response = await request.post<Result<boolean>>(`Wps/MarkUploaded/${year}/${month}`, input);
  return response.data;
}

export async function getKsaWpsHistory(): Promise<Result<KsaWpsSubmissionView[]>> {
  const response = await request.get<Result<KsaWpsSubmissionView[]>>('Wps/History');
  return response.data;
}

// ── UAE WPS Center (MR 340/2026). Every route below is HrOnly and UAE only. ──

/** The list endpoint clamps its page size, so a bigger value would silently truncate. */
const COVERAGE_PAGE_SIZE = 100;

export async function getWpsDueMonth(): Promise<Result<WpsDueMonthView>> {
  const response = await request.get<Result<WpsDueMonthView>>('Wps/DueMonth');
  return response.data;
}

export async function getWpsSettings(): Promise<Result<WpsSettingsView>> {
  const response = await request.get<Result<WpsSettingsView>>('Wps/Settings');
  return response.data;
}

export async function putWpsSettings(input: WpsSettingsInput): Promise<Result<WpsConfirmView>> {
  const response = await request.put<Result<WpsConfirmView>>('Wps/Settings', input);
  return response.data;
}

export async function getWpsPaidOutside(userId: string): Promise<Result<WpsPaidOutsideView>> {
  const response = await request.get<Result<WpsPaidOutsideView>>(`Wps/PaidOutside/${encodeURIComponent(userId)}`);
  return response.data;
}

export async function putWpsPaidOutside(userId: string, input: WpsPaidOutsideInput): Promise<Result<WpsConfirmView>> {
  const response = await request.put<Result<WpsConfirmView>>(`Wps/PaidOutside/${encodeURIComponent(userId)}`, input);
  return response.data;
}

export async function endWpsPaidOutside(userId: string, untilYm: number, confirm: boolean): Promise<Result<WpsConfirmView>> {
  const response = await request.post<Result<WpsConfirmView>>(`Wps/PaidOutside/${encodeURIComponent(userId)}/End`, { untilYm, confirm });
  return response.data;
}

export async function removeWpsPaidOutsidePeriod(id: number, confirm: boolean): Promise<Result<WpsConfirmView>> {
  const response = await request.delete<Result<WpsConfirmView>>(`Wps/PaidOutside/Periods/${id}`, { data: { confirm } });
  return response.data;
}

/** One state per employee for the month. Walks every page, so the people panel can name everyone. */
export async function getWpsCoverage(year: number, month: number): Promise<Result<WpsCoverageView>> {
  const first = await request.get<Result<WpsCoverageView>>(`Wps/Coverage/${year}/${month}`, {
    params: { page: 1, pageSize: COVERAGE_PAGE_SIZE },
  });
  const result = first.data;
  if (result.errors.length > 0 || !result.payload) return result;
  const pages = Math.ceil(result.payload.totalItems / COVERAGE_PAGE_SIZE);
  for (let page = 2; page <= pages; page += 1) {
    const next = await request.get<Result<WpsCoverageView>>(`Wps/Coverage/${year}/${month}`, {
      params: { page, pageSize: COVERAGE_PAGE_SIZE },
    });
    if (next.data.errors.length > 0 || !next.data.payload) return next.data;
    result.payload.items.push(...next.data.payload.items);
  }
  return result;
}

const servedFileName = (headers: Record<string, string>, fallback: string): string => {
  const disposition = headers['content-disposition'] || headers['Content-Disposition'] || '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.trim());
    } catch { /* fall through to the plain name */ }
  }
  return /filename="?([^";]+)"?/i.exec(disposition)?.[1]?.trim() || fallback;
};

const saveBlob = (data: Blob, fileName: string): void => {
  const url = window.URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// A refusal on a blob request arrives as a Blob, which the shared interceptor parses only for an exact
// 'application/json' type. Refusals are read here so their code reaches the screen; 401 and 403 keep the interceptor.
const passRefusals = (status: number): boolean => (status >= 200 && status < 300) || status === 400 || status === 404;

const throwIfRefused = async (response: AxiosResponse<Blob>): Promise<void> => {
  if (response.status === 200) return;
  const body = await response.data.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    throw new Error(body);
  }
  throw parsed;
};

/** Builds a file from those still to file, and saves it under the name the server gave it. */
export async function generateWpsFile(year: number, month: number): Promise<void> {
  const response: AxiosResponse<Blob> = await request.post(`Wps/GenerateSif/${year}/${month}`, {}, {
    responseType: 'blob',
    validateStatus: passRefusals,
  });
  await throwIfRefused(response);
  saveBlob(response.data, servedFileName(response.headers as Record<string, string>, `SIF_${year}_${month}.sif`));
}

/**
 * Serves the stored bytes and changes nothing. The route answers JSON (done = false, naming people) when it has to warn
 * first, so the content type decides which of the two came back. Returns the warnings, or null once the file is saved.
 */
export async function downloadWpsFile(id: number, confirm: boolean): Promise<WpsConfirmWarning[] | null> {
  const response: AxiosResponse<Blob> = await request.get(`Wps/Files/${id}/Download`, {
    params: { confirm },
    responseType: 'blob',
    validateStatus: passRefusals,
  });
  await throwIfRefused(response);
  const headers = response.headers as Record<string, string>;
  if (String(headers['content-type'] || '').includes('application/json')) {
    const body = JSON.parse(await response.data.text()) as Result<WpsConfirmView>;
    return body.payload?.warnings ?? [];
  }
  saveBlob(response.data, servedFileName(headers, `SIF_${id}.sif`));
  return null;
}

export async function markFileSubmitted(id: number, confirm: boolean): Promise<Result<WpsConfirmView>> {
  const response = await request.post<Result<WpsConfirmView>>(`Wps/Files/${id}/MarkSubmitted`, { confirm });
  return response.data;
}

export async function voidWpsFile(
  id: number,
  input: { reason: WpsVoidReason; note?: string; confirm: boolean },
): Promise<Result<WpsConfirmView>> {
  const response = await request.post<Result<WpsConfirmView>>(`Wps/Files/${id}/Void`, input);
  return response.data;
}

export async function releaseWpsLine(
  id: number,
  input: { reason: WpsLineReleaseReason; note?: string; confirm: boolean },
): Promise<Result<WpsConfirmView>> {
  const response = await request.post<Result<WpsConfirmView>>(`Wps/Lines/${id}/Release`, input);
  return response.data;
}

export async function putWpsHold(
  year: number,
  month: number,
  userId: string,
  input: { reason: WpsHoldReason; note?: string; confirm: boolean },
): Promise<Result<WpsConfirmView>> {
  const response = await request.put<Result<WpsConfirmView>>(
    `Wps/Holds/${year}/${month}/${encodeURIComponent(userId)}`,
    input,
  );
  return response.data;
}

export async function releaseWpsHold(year: number, month: number, userId: string): Promise<Result<WpsConfirmView>> {
  const response = await request.delete<Result<WpsConfirmView>>(`Wps/Holds/${year}/${month}/${encodeURIComponent(userId)}`);
  return response.data;
}

export async function holdEveryone(year: number, month: number, confirm: boolean): Promise<Result<WpsConfirmView>> {
  const response = await request.post<Result<WpsConfirmView>>(`Wps/Holds/${year}/${month}/HoldEveryone`, { confirm });
  return response.data;
}

export async function releaseEveryone(year: number, month: number, confirm: boolean): Promise<Result<WpsConfirmView>> {
  const response = await request.post<Result<WpsConfirmView>>(`Wps/Holds/${year}/${month}/ReleaseEveryone`, { confirm });
  return response.data;
}
